import { useQuery } from '@tanstack/react-query'

import { vpcApi, gatewayApi } from '@nebula/shared/services'
import { useAuthStore } from '@nebula/shared/store'

export type InfraStatus = {
  hasVpc: boolean
  vpc: any | null
  isLoadingVpc: boolean
  hasGateway: boolean
  gateway: any | null
  isLoadingGateway: boolean
  isReady: boolean // VPC existe = pode criar VM
  isFullySetup: boolean // VPC + Gateway
  isLoading: boolean
}

export function useInfraStatus(): InfraStatus {
  // Org ativa: mesma fonte do header X-Organization-Id (auth.ts). Sem escopo, estas queries
  // disparam antes de a org resolver, saem sem o header e respondem pela org default — o
  // "Configure sua rede primeiro" acabava afirmado com o estado de OUTRA org, e o resultado
  // ficava cacheado sob uma chave que a org, ao resolver, não invalida. Ver issue #726.
  const activeOrgId = useAuthStore((s) => s.activeOrganizationId)

  const { data: vpc, isLoading: isLoadingVpc } = useQuery({
    queryKey: ['customer-vpc', activeOrgId],
    queryFn: () => vpcApi.getCustomerVPC(),
    enabled: !!activeOrgId,
    staleTime: 30_000,
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 404) return false
      return failureCount < 2
    },
  })

  const { data: gateway, isLoading: isLoadingGateway } = useQuery({
    queryKey: ['gateway', activeOrgId],
    queryFn: () => gatewayApi.getMyGateway(),
    enabled: !!activeOrgId,
    staleTime: 30_000,
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 404) return false
      return failureCount < 2
    },
  })

  const hasVpc = !!vpc
  const hasGateway = !!gateway

  // Enquanto a org não resolveu, `enabled: false` mantém as queries fora de isLoading — mas
  // aqui ainda não se sabe nada da infra, então o estado honesto é "carregando" (é o que
  // segura o InfraSetupBanner e o aviso da quick action de VM).
  const isPendingOrg = !activeOrgId

  return {
    hasVpc,
    vpc: vpc ?? null,
    isLoadingVpc: isPendingOrg || isLoadingVpc,
    hasGateway,
    gateway: gateway ?? null,
    isLoadingGateway: isPendingOrg || isLoadingGateway,
    isReady: hasVpc,
    isFullySetup: hasVpc && hasGateway,
    isLoading: isPendingOrg || isLoadingVpc || isLoadingGateway,
  }
}
